// Client-side usage / spend meter.
//
// Why this exists:
//   BuddyPass is BYOK — the user pays OpenAI directly. They asked for a quiet
//   running tally of "how much did this last call cost?" + "how much have I
//   spent in this browser?" so they can sanity-check spend WITHOUT bouncing
//   to platform.openai.com/usage every time.
//
// Accuracy:
//   • Chat costs are EXACT — OpenAI returns prompt_tokens + completion_tokens
//     in the `usage` field of every chat completion. We multiply by the
//     published gpt-4o-mini rate.
//   • Image costs are ESTIMATED — gpt-image-1 doesn't return a price field.
//     We use OpenAI's published per-image pricing tables (size × quality).
//
// Storage:
//   localStorage key `buddypass_usage_meter_v1`. Cleared from the header pill.
//   Values are USD floats. Nothing leaves the browser.

const STORAGE_KEY = "buddypass_usage_meter_v1";

// --- gpt-4o-mini pricing (USD per 1M tokens) ----------------------------
// Source: https://openai.com/api/pricing/  (current as of 2026-02)
const CHAT_PRICING = {
  "gpt-4o-mini": { input: 0.15, output: 0.6 },
};

// --- gpt-image-1 pricing (USD per image) --------------------------------
// Source: https://openai.com/api/pricing/  (current as of 2026-02)
// Tables are { low, medium, high } per supported size.
const IMAGE_PRICING = {
  "1024x1024": { low: 0.011, medium: 0.042, high: 0.167 },
  "1024x1536": { low: 0.016, medium: 0.063, high: 0.25 },
  "1536x1024": { low: 0.016, medium: 0.063, high: 0.25 },
};

const DEFAULT_STATE = { lastCallUsd: 0, totalUsd: 0, calls: 0 };
const listeners = new Set();

const safeLoad = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return {
      lastCallUsd: Number(parsed.lastCallUsd) || 0,
      totalUsd: Number(parsed.totalUsd) || 0,
      calls: Number(parsed.calls) || 0,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
};

const safeSave = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota exceeded or storage disabled — meter is best-effort */
  }
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch {
      /* listener errors must never break a generation call */
    }
  });
};

export const getUsage = () => safeLoad();

export const subscribeUsage = (fn) => {
  listeners.add(fn);
  // Return unsubscribe so React effects can clean up.
  return () => {
    listeners.delete(fn);
  };
};

export const resetUsage = () => {
  safeSave({ ...DEFAULT_STATE });
};

const bumpBy = (usd) => {
  if (!Number.isFinite(usd) || usd <= 0) return;
  const cur = safeLoad();
  safeSave({
    lastCallUsd: usd,
    totalUsd: cur.totalUsd + usd,
    calls: cur.calls + 1,
  });
};

// Record a chat completion using OpenAI's returned `usage` object (exact).
export const recordChat = ({
  model = "gpt-4o-mini",
  promptTokens = 0,
  completionTokens = 0,
} = {}) => {
  const rates = CHAT_PRICING[model] || CHAT_PRICING["gpt-4o-mini"];
  const usd =
    (Number(promptTokens) / 1_000_000) * rates.input +
    (Number(completionTokens) / 1_000_000) * rates.output;
  bumpBy(usd);
};

// Record an image generation by size + quality (estimated).
export const recordImage = ({
  size = "1024x1536",
  quality = "medium",
  count = 1,
} = {}) => {
  const table = IMAGE_PRICING[size] || IMAGE_PRICING["1024x1536"];
  const per = table[quality] ?? table.medium;
  bumpBy(per * Number(count || 1));
};

// Format a USD amount for the tiny header pill. Adapts precision so a
// $0.0009 call doesn't render as "$0.00" and a $4.23 total doesn't render
// with too many decimals.
export const formatUsd = (n) => {
  const v = Number(n) || 0;
  if (v <= 0) return "$0.00";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  if (v < 1) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(2)}`;
};
